/// <amd-module name="Feed/Legacy/Actions/FeedSettings/SaveViewHandler" />
import { findIndex } from "OfficeFabric/Utilities";

import { announce } from "VSS/Core/Util/Accessibility";
import { IVssPageContext } from "VSS/Platform/Context";

import { Feed, FeedPermission, FeedRole, FeedView, FeedViewType, FeedVisibility } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { CustomSet } from "Feed/Common/Types/CustomSet";
import { SavePermissionsHandler } from "Feed/Legacy/Actions/FeedSettings/SavePermissionsHandler";
import { IFeedSettingsState } from "Feed/Legacy/Components/Settings/IFeedSettingsState";
import { IFeedDataService } from "Feed/Legacy/DataServices/FeedsDataService";

/**
 * User clicked 'Add' button in 'Add view' panel, add that view to grid
 */
export class SaveViewHandler {
    public static async handleAsync(
        state: IFeedSettingsState,
        view: FeedView,
        viewPermissions: FeedPermission[]
    ): Promise<FeedView> {
        if (state.isViewPanelInEditMode) {
            return this.updateViewAsync(state, view, viewPermissions);
        }

        return this.addViewAsync(state, view, viewPermissions);
    }

    private static async addViewAsync(
        state: IFeedSettingsState,
        view: FeedView,
        viewPermissions: FeedPermission[]
    ): Promise<FeedView> {
        const feed = state.feed();
        const feedView = await this.createViewAsync(state, feed, view);
        state.views.push(feedView);

        await this.saveViewPermissionsAsync(
            state.pageContext,
            view.visibility,
            state.viewPermissions,
            feed,
            feedView.id,
            viewPermissions
        );

        announce(PackageResources.SavedAnnouncement);
        return feedView;
    }

    private static async updateViewAsync(
        state: IFeedSettingsState,
        viewToUpdate: FeedView,
        viewPermissions: FeedPermission[]
    ): Promise<FeedView> {
        const feedView = await this.updateFeedViewAsync(state, viewToUpdate);

        // replace view with updated view
        const index = findIndex(state.views, (view: FeedView) => {
            return view.id === feedView.id;
        });

        if (index > -1) {
            state.views[index] = feedView;
        }

        await this.saveViewPermissionsAsync(
            state.pageContext,
            feedView.visibility,
            state.viewPermissions,
            state.feed(),
            feedView.id,
            viewPermissions
        );

        announce(PackageResources.SavedAnnouncement);
        return feedView;
    }

    private static async createViewAsync(state: IFeedSettingsState, feed: Feed, view: FeedView): Promise<FeedView> {
        const feedDataService = state.pageContext.getService<IFeedDataService>("IFeedDataService");
        const projectId = feed.project ? feed.project.id : null;
        const feedView = feedDataService.createFeedViewAsync(projectId, feed.id, view);
        return feedView;
    }

    private static async updateFeedViewAsync(state: IFeedSettingsState, viewToUpdate: FeedView): Promise<FeedView> {
        const view: FeedView = {
            id: viewToUpdate.id,
            // do not send a view name for an implicit view
            name: viewToUpdate.type === FeedViewType.Implicit ? null : viewToUpdate.name,
            visibility: viewToUpdate.visibility
        } as FeedView;

        const feed = state.feed();
        const projectId = feed.project ? feed.project.id : null;
        const feedDataService = state.pageContext.getService<IFeedDataService>("IFeedDataService");
        return feedDataService.updateFeedViewAsync(projectId, feed.id, view, view.id);
    }

    private static async saveViewPermissionsAsync(
        pageContext: IVssPageContext,
        viewVisibility: FeedVisibility,
        viewPermissionsMap: { [key: string]: FeedPermission[] },
        feed: Feed,
        viewId: string,
        permissions: FeedPermission[]
    ): Promise<void> {
        // We only update permissions for private views -- organization and collection views just save the visiblity state
        if (viewVisibility !== FeedVisibility.Private) {
            return;
        }

        // there is no existing permission for new view
        const existingPermissions = viewPermissionsMap[viewId] || [];

        const { permissionsToRetain, permissionsToAdd, permissionsToDelete } = this.splitPermissions(
            existingPermissions,
            permissions
        );

        if (permissionsToAdd.length > 0) {
            await SavePermissionsHandler.setIdentityDescriptorsAsync(pageContext, permissionsToAdd);
        }

        if (permissionsToDelete.length > 0) {
            // set role to None for deleted permissions
            this.updateRoleForPermissionsToDelete(permissionsToDelete);
        }

        const permissionsToSend = [...permissionsToAdd, ...permissionsToDelete];
        if (permissionsToSend.length > 0) {
            await this.setFeedPermissionsAsync(pageContext, feed, viewId, permissionsToSend);

            // TODO: Determine whether we should instead be setting the view permission map value
            // to the permissions returned from setFeedPermissionsAsync
            viewPermissionsMap[viewId] = permissionsToRetain.concat(permissionsToAdd);
        }
    }

    private static async setFeedPermissionsAsync(
        pageContext: IVssPageContext,
        feed: Feed,
        viewId: string,
        permissions: FeedPermission[]
    ): Promise<FeedPermission[]> {
        const feedDataService = pageContext.getService<IFeedDataService>("IFeedDataService");
        return feedDataService.setFeedViewPermissionsAsync(feed, viewId, permissions);
    }

    private static splitPermissions(
        viewPermissions: FeedPermission[],
        permissionsToUpdate: FeedPermission[]
    ): {
        permissionsToRetain: FeedPermission[];
        permissionsToAdd: FeedPermission[];
        permissionsToDelete: FeedPermission[];
    } {
        const permissionsToRetain = [];
        const permissionsToAdd = [];
        const permissionsToDelete = [];

        const viewPermissionsSet = new CustomSet<string>();
        const permissionsToUpdateSet = new CustomSet<string>();
        permissionsToUpdate.forEach((permission: FeedPermission) => {
            permissionsToUpdateSet.add(permission.identityId);
        });

        viewPermissions.forEach((permission: FeedPermission) => {
            viewPermissionsSet.add(permission.identityId);

            if (permissionsToUpdateSet.has(permission.identityId)) {
                permissionsToRetain.push(permission);
                return;
            }

            permissionsToDelete.push(permission);
        });

        permissionsToUpdate.forEach((permission: FeedPermission) => {
            if (viewPermissionsSet.has(permission.identityId)) {
                // existing or deleted, already captured
                return;
            }

            permissionsToAdd.push(permission);
        });

        return { permissionsToRetain, permissionsToAdd, permissionsToDelete };
    }

    private static updateRoleForPermissionsToDelete(permissions: FeedPermission[]): void {
        permissions.forEach((permission: FeedPermission) => {
            permission.role = FeedRole.None;
        });
    }
}
